import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useOutletContext } from "react-router";
import styled from "styled-components";
import { APP_TITLE } from "../../../config/constant";
import FullScreenLoader from "../../common/loaders/FullScreenLoader";
import { CustomSearch } from "../../common/mainApp/mainWindowHeaders/MainWindowHeader";
import SimpleButtonContentHeader from "../../common/mainApp/mainWindowHeaders/SimpleButtonContentHeader";
import FormCard from "../../common/forms/FormCard";
import FormField from "../../common/forms/FormField";
import BIFileUploadCard from "./BIAddComponents/BIFileUploadCard";
import PieChart01 from "@untitled-ui/icons-react/build/esm/PieChart01";
import CoinsStacked01 from "@untitled-ui/icons-react/build/esm/CoinsStacked01";
import * as Library from "../../../utils/Library";
import { requestBuyingIntelPageData } from "./buyingIntelPageApi";
import {
    BUYING_INTEL_PAGE_TYPES,
    DEFAULT_BUYING_CYCLE_ID,
    EMPTY_BUYING_INTEL_FORM_OPTIONS
} from "./buyingIntelPageConfig";
import { parseBuyingIntelAmount } from "./buyingIntelDataUtils";

// #region SCOPED CSS
const StyledPage = styled.div`
    &.biAddPage {
        display: flex;
        flex-direction: column;
    }

    .upload-cards-row {
        display: flex;
        flex-direction: row;
        gap: 16px;
        align-items: stretch;
        flex-wrap: wrap;
    }
`;
// #endregion

const UPLOAD_CARD_CONFIG = [
    { key: "previousSeasonReport", title: "PREVIOUS SEASON REPORT" },
    { key: "newSeasonMaterial", title: "MATERIAL NEW SEASON" },
    { key: "comparableBrandPreviousSeasonReport", title: "PREVIOUS SEASON REPORT (COMP. BRAND)" }
];
// #endregion

export default function BIAddPage() {
    document.title = `${APP_TITLE} - Add Buying Plan`;

    // #region VARS & STATES
    const { MainLayoutProps, layoutType } = useOutletContext();
    const navigations = useSelector(state => state.navigationAccesses);
    const currentCompanyID = localStorage.getItem("currentCompanyID");

    const formCardRef = useRef();
    const formFieldRefs = useRef({});
    const [isLoading, setIsLoading] = useState(true);
    const [formOptions, setFormOptions] = useState(EMPTY_BUYING_INTEL_FORM_OPTIONS);
    const [saveButtonEnabled, setSaveButtonEnabled] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState(() => {
        return Object.fromEntries(UPLOAD_CARD_CONFIG.map(uploadCard => [uploadCard.key, []]));
    });

    const props = {
        layoutType: layoutType,
        setControlBarLeftBtns: MainLayoutProps?.setControlBarLeftBtns,
        setControlBarCenterBtns: MainLayoutProps?.setControlBarCenterBtns,
        setControlBarRightBtns: MainLayoutProps?.setControlBarRightBtns,
        setFilterHeaderProperties: MainLayoutProps?.setFilterHeaderProperties
    };
    // #endregion

    // #region VALIDATION & SAVE
    const checkFormIsValid = (highlightValidation = false) => {
        if (!formCardRef.current) return false;

        const allFieldsAreValid = formCardRef.current.allFieldsAreValid(highlightValidation);
        const budgetValue = parseBuyingIntelAmount(formFieldRefs.current.budget?.getValue?.());
        const budgetIsValid = !!budgetValue && budgetValue > 0;

        const isValid = allFieldsAreValid && budgetIsValid;
        setSaveButtonEnabled(isValid);
        return isValid;
    };

    const handleCreateClicked = () => {
        if (!formCardRef.current || !checkFormIsValid(true)) return;

        const formData = formCardRef.current.getAllFieldValues();
        const budgetValue = parseBuyingIntelAmount(formData?.budget);

        const getSelectionValue = selection => selection?.value ?? selection;
        const requestData = {
            labelBrandID: getSelectionValue(formData.labelBrand),
            buyingSeasonName: getSelectionValue(formData.buyingSeason),
            storeScopeID: getSelectionValue(formData.storeScope),
            comparableSeasonName: getSelectionValue(formData.comparableSeason),
            complementLabelBrandID: getSelectionValue(formData.complementLabelBrand),
            budget: {
                value: budgetValue,
                currency: getSelectionValue(formData.currency)
            },
            additionalNotes: formData.additionalNotes || "",
            files: Object.fromEntries(
                UPLOAD_CARD_CONFIG.map(uploadCard => [
                    uploadCard.key,
                    uploadedFiles[uploadCard.key].map(file => ({
                        fieldName: uploadCard.key,
                        fileName: file.name,
                        mimeType: file.fileObj?.type || "",
                        sizeBytes: file.fileObj?.size || 0
                    }))
                ])
            )
        };
        const fileUploads = Object.fromEntries(
            UPLOAD_CARD_CONFIG.map(uploadCard => [
                uploadCard.key,
                uploadedFiles[uploadCard.key].map(file => file.fileObj).filter(Boolean)
            ])
        );

        Library.showToastMessage({
            type: "warning",
            message: "Buying Plan request prepared. The create endpoint is not available yet."
        });

        return { requestData, fileUploads };
    };

    const handleFilesChanged = (uploadKey, files) => {
        setUploadedFiles(currentFiles => ({
            ...currentFiles,
            [uploadKey]: files
        }));
    };
    // #endregion

    // #region EFFECTS
    useEffect(() => {
        let isMounted = true;
        const handleLoader = loading => {
            if (isMounted) setIsLoading(loading);
        };

        setFormOptions(EMPTY_BUYING_INTEL_FORM_OPTIONS);
        requestBuyingIntelPageData({
            pageType: BUYING_INTEL_PAGE_TYPES.ADD,
            companyID: currentCompanyID,
            setLoader: handleLoader,
            onError: () => handleLoader(false)
        }).then(data => {
            if (!isMounted || data === undefined) return;

            const options = data?.formOptions || {};
            setFormOptions({
                labelBrands: Array.isArray(options.labelBrands) ? options.labelBrands : [],
                seasons: Array.isArray(options.seasons) ? options.seasons : [],
                storeScopes: Array.isArray(options.storeScopes) ? options.storeScopes : [],
                currencies: Array.isArray(options.currencies) ? options.currencies : []
            });
        });

        return () => {
            isMounted = false;
        };
    }, [currentCompanyID]);

    useEffect(() => {
        setupHeaders(props, saveButtonEnabled, handleCreateClicked, navigations);
    }, [props?.layoutType, navigations, saveButtonEnabled]);
    // #endregion

    // #region FORM FIELDS DEFINITION
    const formFields = [
        <FormField
            type="dropdown"
            title="Label Brand"
            placeholder="Select Label Brand"
            requiredType="mandatory"
            varName="labelBrand"
            dropdownData={formOptions.labelBrands}
            ref={el => (formFieldRefs.current.labelBrand = el)}
        />,
        <FormField
            type="dropdown"
            title="Buying season"
            placeholder="Select Season"
            requiredType="mandatory"
            varName="buyingSeason"
            dropdownData={formOptions.seasons}
            ref={el => (formFieldRefs.current.buyingSeason = el)}
        />,
        <FormField
            type="dropdown"
            title="Store scope"
            placeholder="Select stores"
            requiredType="mandatory"
            varName="storeScope"
            dropdownData={formOptions.storeScopes}
            ref={el => (formFieldRefs.current.storeScope = el)}
        />,
        <FormField
            type="dropdown"
            title="Comparable season"
            placeholder="Select season"
            requiredType="mandatory"
            varName="comparableSeason"
            dropdownData={formOptions.seasons}
            ref={el => (formFieldRefs.current.comparableSeason = el)}
        />,
        <FormField
            type="dropdown"
            title="Complement Label Brand"
            placeholder="Select Label Brand"
            requiredType="mandatory"
            varName="complementLabelBrand"
            dropdownData={formOptions.labelBrands}
            ref={el => (formFieldRefs.current.complementLabelBrand = el)}
        />,
        <FormField
            type="text"
            title="Budget"
            placeholder="Insert budget"
            requiredType="mandatory"
            varName="budget"
            ref={el => (formFieldRefs.current.budget = el)}
            leftContent={<PieChart01 width={16} height={16} color={"#7C868B"} />}
        />,
        <FormField
            type="dropdown"
            title="Currency"
            placeholder="Select currency"
            requiredType="mandatory"
            varName="currency"
            dropdownData={formOptions.currencies}
            ref={el => (formFieldRefs.current.currency = el)}
            leftContent={<CoinsStacked01 width={16} height={16} color={"#7C868B"} />}
        />,
        <FormField
            type="text"
            title="Additional notes"
            placeholder="Add notes"
            requiredType="simple"
            varName="additionalNotes"
            ref={el => (formFieldRefs.current.additionalNotes = el)}
        />
    ];
    // #endregion

    return (
        <>
            <FullScreenLoader isLoading={isLoading} />
            <StyledPage className="biAddPage">
                {/* Top Form Card */}
                <FormCard
                    title="REQUEST BUYING PLAN"
                    ref={formCardRef}
                    numOfFieldsPerLine={4}
                    showDefaultRightLegends={true}
                    formFields={formFields}
                    formFieldRefs={formFieldRefs.current}
                    onFormChanged={() => checkFormIsValid(false)}
                />

                {/* Bottom Upload Cards */}
                <div className="upload-cards-row">
                    {UPLOAD_CARD_CONFIG.map(uploadCard => (
                        <BIFileUploadCard
                            key={uploadCard.key}
                            title={uploadCard.title}
                            files={uploadedFiles[uploadCard.key]}
                            onFilesChanged={files => handleFilesChanged(uploadCard.key, files)}
                        />
                    ))}
                </div>
            </StyledPage>
        </>
    );
}

// #region SMALL COMPONENTS
function setupHeaders(props, saveButtonEnabled, handleCreateClicked, navigations) {
    const createMode = navigations?.navigationAccesses?.buyingIntel?.createModeOnly;

    props.setControlBarLeftBtns([
        {
            customSearch: <CustomSearch enabled={false} placeholder="Brand, season recommendation or order" />
        }
    ]);
    props.setControlBarCenterBtns([
        { to: "/buying-intel", active: true, disabled: false, title: "BUYING CYCLES" },
        { to: "/buying-intel/portfolio", active: false, title: "PORTFOLIO", disabled: true },
        { to: "/buying-intel/actions", active: false, title: "ACTIONS", disabled: true }
    ]);
    props.setControlBarRightBtns([
        { to: "/buying-intel", active: false, disabled: createMode, title: "View", icon: "eye" },
        {
            to: "/buying-intel/add",
            active: true,
            disabled: false,
            title: "Add",
            icon: "plus"
        },
        {
            to: `/buying-intel/overview/${DEFAULT_BUYING_CYCLE_ID}`,
            active: false,
            disabled: true,
            title: "Overview",
            icon: "presentation-chart-01"
        },
        {
            to: `/buying-intel/buyplan/${DEFAULT_BUYING_CYCLE_ID}`,
            active: false,
            disabled: true,
            title: "Buy plan",
            icon: "lightbulb-05"
        },
        {
            to: `/buying-intel/order/${DEFAULT_BUYING_CYCLE_ID}`,
            active: false,
            disabled: true,
            title: "Order",
            icon: "scale-02"
        },
        {
            to: `/buying-intel/forecast/${DEFAULT_BUYING_CYCLE_ID}`,
            active: false,
            disabled: true,
            title: "Forecast",
            icon: "line-chart-up-01"
        }
    ]);

    props.setFilterHeaderProperties({
        customContentHeader: (
            <SimpleButtonContentHeader btnTitle="CREATE" btnEnabled={saveButtonEnabled} onClick={handleCreateClicked} />
        )
    });
}
// #endregion
